import React, { useState } from 'react';

import dayjs from '~/timeInit';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { DateRangePicker, LocalizationProvider, SingleInputDateRangeField } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';

import type { Campaign } from './data';
import type { Filters } from './model';
import { dateProblem, filterChanges } from './reportState';
import { defaultPeriods } from './selectionModel';

const DEFAULTS = {
  refund: 'Before Refund' as const,
  currency: 'Vendor Currency',
  room: 'All',
  packageType: 'All',
  booking: 'All',
  region: 'Strategic Territory',
};
type SecondaryKey = keyof typeof DEFAULTS;
type Props = {
  filters: Filters;
  rooms: Array<string>;
  campaigns: Campaign[];
  onClose: () => void;
  onApply: (changes: Record<string, string>) => void;
};

/** Mounted afresh on open: drafts never update the URL, data request or export scope. */
export default function LPCFilterDialog({ filters, rooms, campaigns, onClose, onApply }: Props) {
  const [initial] = useState(() => ({ ...filters }));
  const [draft, setDraft] = useState(initial);
  const problem = dateProblem(draft);
  const campaign = campaigns.find((value) => value.offer_bk === draft.current);
  const fields: Array<{ key: SecondaryKey; label: string; values: Array<string> }> = [
    { key: 'refund', label: 'Refund basis', values: ['Before Refund', 'After Refund'] },
    { key: 'currency', label: 'Currency', values: ['Vendor Currency', 'AUD'] },
    { key: 'room', label: 'Room type', values: ['All', ...rooms] },
    { key: 'packageType', label: 'Package type', values: ['All', 'Base', 'Indulgence'] },
    {
      key: 'booking',
      label: 'Check-in booking type',
      values: ['All', 'BNBL Bookings', 'Bookings Before Today', 'Upcoming Bookings Only'],
    },
    {
      key: 'region',
      label: 'Regional share geography',
      values: ['Country', 'Area Level 1', 'Area Level 2', 'Strategic Territory'],
    },
  ];
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="lpc-filter-title">
      <DialogTitle id="lpc-filter-title">Dates & filters</DialogTitle>
      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack gap={2} sx={{ pt: 1, pb: 2 }}>
            <Button
              sx={{ alignSelf: 'start' }}
              disabled={!campaign}
              onClick={() =>
                campaign &&
                setDraft((previous) => ({
                  ...previous,
                  ...defaultPeriods(
                    campaign,
                    new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(new Date()),
                  ),
                }))
              }
            >
              Use source default periods · 56 days each
            </Button>
            {(['current', 'comparison'] as const).map((side) => {
              const start = side === 'current' ? 'from' : 'cmpFrom';
              const end = side === 'current' ? 'to' : 'cmpTo';
              return (
                <DateRangePicker
                  key={side}
                  slots={{ field: SingleInputDateRangeField }}
                  label={side === 'current' ? 'Current date range' : 'Comparison date range'}
                  value={[draft[start] ? dayjs(draft[start]) : null, draft[end] ? dayjs(draft[end]) : null]}
                  onChange={(range) =>
                    setDraft((previous) => ({
                      ...previous,
                      [start]: range[0]?.format('YYYY-MM-DD') ?? '',
                      [end]: range[1]?.format('YYYY-MM-DD') ?? '',
                    }))
                  }
                  format="DD MMM YYYY"
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              );
            })}
          </Stack>
        </LocalizationProvider>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, pt: 1 }}>
          {fields.map(({ key, label, values }) => (
            <TextField
              key={key}
              select
              fullWidth
              size="small"
              label={label}
              value={draft[key]}
              onChange={(event) => setDraft((previous) => ({ ...previous, [key]: event.target.value }))}
            >
              {values.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          ))}
        </Box>
        {problem && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {problem}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setDraft((previous) => ({ ...previous, ...DEFAULTS }))} sx={{ mr: 'auto' }}>
          Reset filters
        </Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!!problem}
          onClick={() => {
            onApply(filterChanges(initial, draft));
            onClose();
          }}
        >
          Apply filters
        </Button>
      </DialogActions>
    </Dialog>
  );
}
